import { useEffect, useMemo } from "react";

import { flattenConversations, useConversations } from "~/ai-chat/data-source/use-conversations";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import type { Conversation } from "~/ai-chat/model/conversation";
import type { ChatTab } from "~/ai-chat/ui/chat-tabs";

export interface ChatTabsState {
  tabs: ChatTab[];
  // Stable key of the on-screen tab (empty tab's client key, else the conversation id).
  // Changes on every real switch but not on an empty-tab keystroke — key the composer by
  // it so its draft resets per tab instead of leaking across conversations.
  activeKey: string;
  switchTo: (key: string) => void;
  closeTab: (key: string) => void;
  startNew: () => void;
}

// Open-chats state for the tab strip. Saved conversations opened this session are
// keyed by their id; unsaved new chats get a client-side key so several empty tabs
// coexist and survive a switch. Switching never stops a running chat (they stream in
// parallel — see useChatSend), and the status dot reads the live `runningConversationIds`
// so it flips to amber while a chat is processing. Pair with `useHydrateActiveConversation`.
export function useChatTabs(): ChatTabsState {
  const {
    activeConversationId,
    setActiveConversationId,
    chatTitle,
    setChatTitle,
    startNewConversation,
    clearMessages,
    markConversationSynced,
    isConversationUnsynced,
    runningConversationIds,
    tabInitialKey: initialKey,
    tabOpenIds: openIds,
    setTabOpenIds: setOpenIds,
    tabEmptyKeys: emptyKeys,
    setTabEmptyKeys: setEmptyKeys,
    tabActiveEmptyKey: activeEmptyKey,
    setTabActiveEmptyKey: setActiveEmptyKey,
  } = useAiChat();
  const conversationsQuery = useConversations({ q: "", includeArchived: false });
  const conversationsById = useMemo(() => {
    const map = new Map<string, Conversation>();
    for (const conversation of flattenConversations(conversationsQuery.data)) map.set(conversation.id, conversation);
    return map;
  }, [conversationsQuery.data]);

  // Reconcile external changes to the active conversation into the tab list: a first
  // send turns the active empty tab into a saved one; a sidebar select adds a saved
  // tab; a sidebar "New" (active → null with no empty tab) opens a fresh empty tab.
  useEffect(() => {
    if (activeConversationId !== null) {
      if (activeEmptyKey !== null) {
        setEmptyKeys((prev) => prev.filter((key) => key !== activeEmptyKey));
        setActiveEmptyKey(null);
      }
      setOpenIds((prev) => (prev.includes(activeConversationId) ? prev : [...prev, activeConversationId]));
    } else if (activeEmptyKey === null) {
      const key = crypto.randomUUID();
      setEmptyKeys((prev) => [...prev, key]);
      setActiveEmptyKey(key);
    }
  }, [activeConversationId, activeEmptyKey, setOpenIds, setEmptyKeys, setActiveEmptyKey]);

  const switchTo = (key: string) => {
    if (emptyKeys.includes(key)) {
      if (key === activeEmptyKey) return;
      if (activeConversationId !== null) markConversationSynced(activeConversationId);
      setActiveEmptyKey(key);
      setActiveConversationId(null);
      clearMessages();
    } else {
      if (key === activeConversationId && activeEmptyKey === null) return;
      if (activeConversationId !== null) markConversationSynced(activeConversationId);
      setActiveEmptyKey(null);
      // Drop the departing transcript so the switched-to conversation can't send / edit /
      // retry against the previous one during the hydration gap. Skip it when the target
      // has a live (unsynced) reply — hydration won't refill that, so clearing would blank it.
      if (!isConversationUnsynced(key)) clearMessages();
      setActiveConversationId(key);
      setChatTitle(conversationsById.get(key)?.title ?? null);
    }
  };

  const startNew = () => {
    if (activeConversationId !== null) markConversationSynced(activeConversationId);
    const key = crypto.randomUUID();
    setEmptyKeys((prev) => [...prev, key]);
    setActiveEmptyKey(key);
    startNewConversation();
  };

  // Move to the first available tab after a close, or a fresh empty chat if none remain.
  const switchToFirst = (primary: string | null, secondary: string | null) => {
    const target = primary ?? secondary;
    if (target !== null) switchTo(target);
    else startNew();
  };

  const closeTab = (key: string) => {
    if (emptyKeys.includes(key)) {
      const remaining = emptyKeys.filter((openKey) => openKey !== key);
      setEmptyKeys(remaining);
      if (key === activeEmptyKey) switchToFirst(remaining.at(-1) ?? null, openIds.at(-1) ?? null);
    } else {
      const remaining = openIds.filter((id) => id !== key);
      setOpenIds(remaining);
      if (key === activeConversationId && activeEmptyKey === null) {
        switchToFirst(remaining.at(-1) ?? null, emptyKeys.at(-1) ?? null);
      }
    }
  };

  const savedTabs: ChatTab[] = openIds.map((id) => {
    const isActive = id === activeConversationId && activeEmptyKey === null;
    return {
      key: id,
      title: (isActive ? chatTitle : conversationsById.get(id)?.title) ?? "New chat",
      status: runningConversationIds.has(id) ? "running" : (conversationsById.get(id)?.status ?? null),
      isActive,
    };
  });
  const emptyTabs: ChatTab[] = emptyKeys.map((key) => ({
    key,
    title: (key === activeEmptyKey ? chatTitle : null) ?? "New chat",
    status: null,
    isActive: key === activeEmptyKey,
  }));

  const activeKey = activeEmptyKey ?? activeConversationId ?? initialKey;

  return { tabs: [...savedTabs, ...emptyTabs], activeKey, switchTo, closeTab, startNew };
}
